import { memo, type PointerEvent as ReactPointerEvent } from 'react'
import type { ChecklistItem } from '../../types/schema'
import { useNls } from '../../nls/nlsProvider'
import { Pressable } from '../Common/Pressable'

type GateNodeProps = {
  node: ChecklistItem
  x: number
  y: number
  diameter: number
  gateInputCount: number
  isGateAnd: boolean
  isGateOpen: boolean
  canEditStructure: boolean
  onNodePointerDown: (event: ReactPointerEvent<HTMLElement>, node: ChecklistItem) => void
  onAnchorPointerDown: (event: ReactPointerEvent<HTMLElement>, node: ChecklistItem, anchor: 'bottom') => void
  /** Gate delete callback ("open" no longer gates deletion trigger; canvas enforces connected-gate rules) */
  onDeleteGate?: (node: ChecklistItem) => void
  onDeleteOpenGate?: (node: ChecklistItem) => void
}

const GateNodeComponent = ({
  node,
  x,
  y,
  diameter,
  gateInputCount,
  isGateAnd,
  isGateOpen,
  onNodePointerDown,
  onAnchorPointerDown,
  onDeleteGate,
  onDeleteOpenGate,
}: GateNodeProps) => {
  const { t } = useNls()
  const gateLabel = isGateAnd ? 'AND' : 'OR'

  return (
  <article
    data-board-gate-id={node.id}
    data-orbit-target="checklist-gate"
    data-checklist-id={node.id}
    data-orbit-title={node.title}
      data-gate-input-count={gateInputCount}
      className={`checklistGate ${isGateAnd ? 'isGateAnd' : 'isGateOr'} ${isGateOpen ? 'isGateOpen' : 'isGateConnected'}`}
      style={{
        left: x,
        top: y,
        width: diameter,
        height: diameter,
      }}
    >
      <Pressable
        className="checklistGateInZone"
        aria-label={t('NLSChecklistAriaIncomingWireAnchor')}
        data-board-anchor-item-id={node.id}
        data-role="incoming"
        onPointerDown={(event) => onNodePointerDown(event, node)}
      />
      <Pressable
        className="checklistGateOutZone"
        aria-label={t('NLSChecklistBoardNodeGateOutput', { gate: gateLabel })}
        title={t('NLSChecklistBoardNodeGateOutputHint')}
        data-board-anchor-item-id={node.id}
        data-role="outgoing"
        onPointerDown={(event) => onAnchorPointerDown(event, node, 'bottom')}
      />
      <Pressable
        className="checklistGateCenterPill"
        aria-label={t('NLSChecklistAriaDeleteGate')}
        title={t('NLSChecklistAriaDeleteGate')}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation()
          if (onDeleteGate) {
            onDeleteGate(node)
          } else if (onDeleteOpenGate) {
            onDeleteOpenGate(node)
          }
        }}
      >
        <span className="checklistGateGlyph" aria-hidden="true">{isGateAnd ? '&' : '||'}</span>
        <span className="checklistGateGlyphX" aria-hidden="true">×</span>
      </Pressable>
    </article>
  )
}

export const GateNode = memo(GateNodeComponent, (prev, next) =>
  prev.node === next.node
  && prev.x === next.x
  && prev.y === next.y
  && prev.diameter === next.diameter
  && prev.gateInputCount === next.gateInputCount
  && prev.isGateAnd === next.isGateAnd
  && prev.isGateOpen === next.isGateOpen
  && prev.canEditStructure === next.canEditStructure
  && prev.onNodePointerDown === next.onNodePointerDown
  && prev.onAnchorPointerDown === next.onAnchorPointerDown
  && prev.onDeleteGate === next.onDeleteGate
  && prev.onDeleteOpenGate === next.onDeleteOpenGate
)
